import { gql, subscribe } from '@polleverywhere/graphql-client'
import toSnakeCase from '../../../util/toSnakeCase'

/**
 * Archive all results that were created before the provided date
 *
 * @this Discourse
 * @param {string} before Timestamp (Date parseable) to compare results against
 */
function onActionArchive (before) {
  const compareTo = new Date(before).getTime()
  const resultsToMaintain = this._results.filter((result) => {
    const created = new Date(result.get('created_at'))
    return compareTo < created
  })
  this._results.set(resultsToMaintain)
}

function logSubscriptionError (response) {
  console.warn('GraphQL subscription error', response.error)
  window.Rollbar?.warning('GraphQL subscription error', response.error)
}

/**
 * @type {SubscriptionDefinition[]}
 */
export const SUBSCRIPTIONS = [
  {
    queryName: 'presentersDiscourseInstanceUpdated',
    params: function (model) {
      return {
        query: gql`
          subscription presentersDiscourseInstanceUpdated($presenterKey: ID!) {
            presentersDiscourseInstanceUpdated(presenterKey: $presenterKey) {
              activated
              autocensor
              countdownSeconds
              displayState
              emojiFilter
              featuredResultId
              locale
              moderationEnabled
              ownedByPresenter
              presenter {
                competitionScreenNamesEnabled
                login
                name
                scheduledActions
                screenNamesEnabled
                sessionKeyword
              }
              resultsCount
              sharedModeration
              showRecent
              state
            }
        }`,
        variables: {
          presenterKey: model.get('presenter_key')
        }
      }
    },
    onData: function (data) {
      this.set(this.parse(data))
    }
  }, {
    queryName: 'presentersDiscoursePrototypeUpdated',
    params: function (model) {
      return {
        query: gql`
          subscription presentersDiscoursePrototypeUpdated($presenterSharedKey: ID!) {
            presentersDiscoursePrototypeUpdated(presenterSharedKey: $presenterSharedKey) {
              anonymous
              downvotingDisabled
              editableAnswers
              groupId
              id
              legacyVisualSettings
              maxVotes
              ownerId
              position
              registeredParticipantsOnly
              showBranding
              showParticipants
              simpleKeywords
              smsEnabled
              startAt
              stopAt
              title
              titleHidden
              type
              webEnabled
            }
          }`,
        variables: {
          presenterSharedKey: model.get('presenter_shared_key')
        }
      }
    },
    onData: function (data) {
      this.set(this.parse(data))
    }
  }, {
    queryName: 'presentersPollActions',
    params: function (model) {
      return {
        query: gql`
          subscription presentersPollActions($presenterKey: ID!) {
            presentersPollActions(presenterKey: $presenterKey) {
              __typename
              ... on PollActionArchive {
                actionAt
              }
            }
          }
        `,
        variables: {
          presenterKey: model.get('presenter_key')
        }
      }
    },
    onData: function (data) {
      const type = data.__typename ?? data.typename // __ prefix may be dropped by snake-case (https://github.com/blakeembrey/change-case/issues/56)
      if (type === 'PollActionArchive') {
        onActionArchive.call(this, data.action_at)
      }
    }
  }, {
    queryName: 'presentersDiscourseResultCreated',
    params: function (model) {
      return {
        query: gql`
          subscription presentersDiscourseResultCreated($presenterKey: ID!) {
            presentersDiscourseResultCreated(presenterKey: $presenterKey) {
              id
              value
              approved
              presentable
              participantIdentifier
              votes {
                upvotes
                downvotes
                netVotes
              }
            }
          }
        `,
        variables: {
          presenterKey: model.get('presenter_key')
        }
      }
    },
    onData: function (result) {
      hoistVotes(result)
      this.results.add(result, { merge: true })
    }
  }, {
    queryName: 'presentersDiscourseResultUpdated',
    params: function (model) {
      return {
        query: gql`
          subscription presentersDiscourseResultUpdated($presenterKey: ID!) {
            presentersDiscourseResultUpdated(presenterKey: $presenterKey) {
              id
              value
              approved
              presentable
              participantIdentifier
              votes {
                upvotes
                downvotes
                netVotes
              }
            }
          }
        `,
        variables: {
          presenterKey: model.get('presenter_key')
        }
      }
    },
    onData: function (result) {
      hoistVotes(result)
      this.results.add(result, { merge: true })
    }
  }, {
    queryName: 'presentersDiscourseResultDeleted',
    params: function (model) {
      return {
        query: gql`
          subscription presentersDiscourseResultDeleted($presenterKey: ID!) {
            presentersDiscourseResultDeleted(presenterKey: $presenterKey) {
              id
            }
          }
        `,
        variables: {
          presenterKey: model.get('presenter_key')
        }
      }
    },
    onData: function (result) {
      this.results.remove(result.id)
    }
  }, {
    queryName: 'presentersResultVotesChanged',
    params: function (model) {
      return {
        query: gql`
          subscription presentersResultVotesChanged($presenterKey: ID!) {
            presentersResultVotesChanged(presenterKey: $presenterKey) {
              resultId
              netVotes
              upvotes
              downvotes
            }
          }
        `,
        variables: {
          presenterKey: model.get('presenter_key')
        }
      }
    },
    onData: function (data) {
      const result = this.results.findWhere({ id: data.result_id })

      result?.set('net_votes', data.net_votes)
      result?.set('upvotes', data.upvotes)
      result?.set('downvotes', data.downvotes)
    }
  }
]

/**
 * @param {Discourse} model Discourse model
 * @param {SubscriptionDefinition} def Subscription definition
 * @returns {Promise<{unsubscribe: Function}>} Subscription object to allow unsubscribing
 */
export function createSubscription (model, def) {
  const dataHandler = def.onData.bind(model)
  const subscription = subscribe(def.params(model), (response) => {
    if (response.error) return logSubscriptionError(response)

    const parsedData = toSnakeCase(response.data[def.queryName])
    dataHandler(parsedData)
  })
  return subscription
}

/**
 * @param {Discourse} model Discourse model
 * @returns {Promise<{unsubscribe: Function}>[]} Subscription objects to allow unsubscribing
 */
export function createAllSubscriptions (model) {
  return SUBSCRIPTIONS.map((subscriptionDef) => createSubscription(model, subscriptionDef))
}

function hoistVotes (result) {
  if (result.votes) {
    // API nests a 'votes' object, but we need it on the root
    result.net_votes = result.votes.net_votes
    result.upvotes = result.votes.upvotes
    result.downvotes = result.votes.downvotes
    delete result.votes
  }
}
